import { useCallback, useState } from 'react';

import {
  type EmployeeImportFormat,
  useImportEmployeesMutation,
  useImportManagerialAccountingMutation,
} from '@/common/graphql';
import { useGoogleOfflineToken } from '@/common/hooks/general/useGoogleOfflineToken';
import { ApiUtils } from '@/common/utils';

import { type ImportModalFormData, MANAGERIAL_ACCOUNTING_IMPORT_TYPE } from './types';

interface UseImportModalSubmitOptions {
  onSubmitSuccess: () => void;
}

/**
 * Owns the bulk-import submission lifecycle shared by every entry in the import modal:
 * Google offline-token verification, dispatch to the correct import mutation
 * (Employee vs. Managerial Accounting), and the loading flag that gates the form
 * while a request is in flight (also written to — but not read by — the
 * template-export flow; see `setTemplateExportInProgress`).
 *
 * @returns `loading` — whether a submission or template export is in flight (gates the form);
 *          `setTemplateExportInProgress` — lets the consumer flip the loading flag for the
 *            template-export flow, which this hook does not own (the export runs through a
 *            sibling `ImportTemplateAction` component, not the import mutation path);
 *          `handleUpload` — form submit handler that verifies the token and dispatches the import.
 */
export const useImportModalSubmit = ({ onSubmitSuccess }: UseImportModalSubmitOptions) => {
  const [loading, setLoading] = useState(false);
  const { verifyToken } = useGoogleOfflineToken();

  const [importEmployees] = useImportEmployeesMutation({
    refetchQueries: ['listImportEmployeeHistories'],
    onError(error) {
      ApiUtils.handleApiError(error);
    },
    onCompleted: onSubmitSuccess,
  });

  const [importManagerialAccounting] = useImportManagerialAccountingMutation({
    onError(error) {
      ApiUtils.handleApiError(error);
    },
    onCompleted: onSubmitSuccess,
  });

  const handleUpload = useCallback(
    async (data: ImportModalFormData) => {
      setLoading(true);

      try {
        await verifyToken(import.meta.env.VITE_EMPLOYEE_CALLBACK_URL);

        if (data.action === MANAGERIAL_ACCOUNTING_IMPORT_TYPE) {
          await importManagerialAccounting({
            variables: { url: data.link, timezoneOffset: new Date().getTimezoneOffset() },
          });
        } else {
          await importEmployees({
            variables: {
              format: data.action as EmployeeImportFormat,
              url: data.link,
              timezoneOffset: new Date().getTimezoneOffset(),
            },
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [importEmployees, importManagerialAccounting, verifyToken],
  );

  return { loading, setTemplateExportInProgress: setLoading, handleUpload };
};
